<template>
  <div class="message-wrapper" :class="{ 'message-own': isOwn }">
    <!-- Avatar para mensagens recebidas -->
    <div v-if="!isOwn" class="message-avatar">
      <div class="avatar-circle" :class="{ 'avatar-admin': userType === 'Backoffice::User' }">
        <v-icon v-if="userType === 'Backoffice::User'" color="white" size="16">support_agent</v-icon>
        <v-icon v-else color="white" size="16">person</v-icon>
      </div>
    </div>

    <!-- Container da mensagem -->
    <div class="message-container">
      <!-- Informações do autor (apenas para mensagens recebidas) -->
      <div v-if="!isOwn && userType === 'Backoffice::User'" class="message-author">
        {{ userName }}
        <span class="admin-badge">
          <v-icon size="10">verified</v-icon>
          Suporte
        </span>
      </div>

      <!-- Bolha da mensagem -->
      <div class="message-bubble" :class="{ 'bubble-own': isOwn, 'bubble-admin': !isOwn && userType === 'Backoffice::User' }">
        <!-- Conteúdo da mensagem de texto -->
        <div v-if="!isFile && message" class="message-content" v-html="message"></div>

        <!-- Arquivo legacy (campo message quando isFile = true) -->
        <div v-if="isFile && message" class="message-content">
          <div class="file-attachment">
            <img
              v-if="isImage(message)"
              :src="getFileUrl(message)"
              class="file-image"
              @click="openImage(getFileUrl(message))"
              alt="Imagem enviada"
            />
            <div v-else class="file-download" @click="downloadFile(getFileUrl(message))">
              <v-icon size="20">attachment</v-icon>
              <span>Arquivo anexado</span>
              <v-icon size="16">download</v-icon>
            </div>
          </div>
        </div>

        <!-- Arquivos anexados (sistema novo) -->
        <div v-if="files && files.length > 0" class="message-files">
          <div v-for="file in files" :key="file.id || file.url" class="file-attachment">
            <img
              v-if="isImage(file.url)"
              :src="getFileUrl(file.url)"
              class="file-image"
              @click="openImage(getFileUrl(file.url))"
              :alt="file.name || 'Imagem'"
            />
            <div v-else class="file-download" @click="downloadFile(getFileUrl(file.url))">
              <v-icon size="20">attachment</v-icon>
              <span>{{ file.name || 'Arquivo' }}</span>
              <v-icon size="16">download</v-icon>
            </div>
          </div>
        </div>

        <!-- Horário da mensagem -->
        <div class="message-time">
          {{ formatTime(created_at) }}
          <v-icon v-if="isOwn" size="14" class="read-status">done_all</v-icon>
        </div>
      </div>
    </div>

    <!-- Avatar para mensagens próprias -->
    <div v-if="isOwn" class="message-avatar message-avatar-own">
      <div class="avatar-circle avatar-own">
        <span class="avatar-text">EU</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { buildFileUrl } from "@/utils/urlHelper";

export default {
  name: 'Message',
  data() {
    return {
      url: ""
    }
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    message: {
      type: String,
      required: true,
    },
    created_at: {
      type: String,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    userType: {
      type: String,
    },
    isFile: {
      type: Boolean,
    },
    files: {
      type: Array,
    },
    isOwn: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    // Debug: log component data to understand file structure
    console.log('💬 Message component mounted:', {
      id: this.id,
      isFile: this.isFile,
      files: this.files,
      message: this.message
    });

    // If there are files, log their structure
    if (this.files && this.files.length > 0) {
      console.log('📎 Files data:', this.files);
      this.files.forEach((file, index) => {
        console.log(`📄 File ${index}:`, {
          url: file.url,
          name: file.name,
          isImage: this.isImage(file.url)
        });
      });
    }
  },
  methods: {
    getFileUrl(fileUrl) {
      // Use the centralized URL helper to build the correct file URL
      return buildFileUrl(fileUrl);
    },
    formatMessageContent(message, isFile){
      if(isFile){
        return `<a href="${message}">${message}</a>`;
      }else{
        return message;
      }
    },
    formatTime(date) {
      return moment(date).format('HH:mm');
    },
    isImage(url) {
      if (!url) return false;

      // Convert to string if it's not already
      const urlString = String(url).toLowerCase();

      // Check for common image extensions
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

      // Check if URL contains any image extension
      const hasImageExtension = imageExtensions.some(ext => urlString.includes(ext));

      // For chat_files URLs, also check for common image patterns
      const isLikelyImage = hasImageExtension ||
                           urlString.includes('image/') ||
                           urlString.match(/\.(jpe?g|png|gif|webp|bmp|svg)(\?|$|&)/i);

      console.log('🖼️ isImage check:', { url: urlString, hasImageExtension, isLikelyImage });

      return isLikelyImage;
    },
    openImage(url) {
      // Enhanced image opening with modal support and download option
      const fullUrl = this.getFileUrl(url);

      // Create a modal overlay for better image viewing
      const modal = document.createElement('div');
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      `;

      const img = document.createElement('img');
      img.src = fullUrl;
      img.style.cssText = `
        max-width: 90%;
        max-height: 90%;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
      `;

      // Download button
      const downloadBtn = document.createElement('button');
      downloadBtn.innerHTML = '📥 Download';
      downloadBtn.style.cssText = `
        position: absolute;
        top: 20px;
        right: 20px;
        padding: 10px 15px;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-weight: bold;
        color: #333;
      `;

      downloadBtn.onclick = (e) => {
        e.stopPropagation();
        this.downloadFile(fullUrl);
      };

      // Close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '✕';
      closeBtn.style.cssText = `
        position: absolute;
        top: 20px;
        left: 20px;
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        font-size: 18px;
        font-weight: bold;
        color: #333;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      closeBtn.onclick = (e) => {
        e.stopPropagation();
        document.body.removeChild(modal);
      };

      modal.appendChild(img);
      modal.appendChild(downloadBtn);
      modal.appendChild(closeBtn);

      // Close on background click
      modal.onclick = () => {
        document.body.removeChild(modal);
      };

      // Prevent image click from closing
      img.onclick = (e) => {
        e.stopPropagation();
      };

      document.body.appendChild(modal);
    },
    downloadFile(url) {
      // Enhanced download function with better file handling
      const fullUrl = this.getFileUrl(url);

      try {
        // Try to extract filename from URL
        const urlParts = fullUrl.split('/');
        const filename = urlParts[urlParts.length - 1] || 'download';

        // Create temporary link for download
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = filename;
        link.target = '_blank';

        // Append to body temporarily for Firefox compatibility
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('✅ Download initiated for:', filename);
      } catch (error) {
        console.error('❌ Download failed:', error);
        // Fallback to opening in new tab
        window.open(fullUrl, '_blank');
      }
    }
  },
};
</script>

<style scoped>
/* Container principal da mensagem */
.message-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 16px;
  max-width: 80%;
  animation: fadeInUp 0.3s ease-out;
}

.message-wrapper.message-own {
  flex-direction: row-reverse;
  margin-left: auto;
  margin-right: 0;
}

.message-wrapper:not(.message-own) {
  margin-right: auto;
  margin-left: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Avatar */
.message-avatar {
  flex-shrink: 0;
  margin-bottom: 4px;
}

.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #bbb;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.avatar-circle.avatar-admin {
  background: var(--v-primary-base, linear-gradient(135deg, #1976d2 0%, #1565c0 100%));
}

.avatar-circle.avatar-own {
  background: var(--v-success-base, linear-gradient(135deg, #4caf50 0%, #388e3c 100%));
}

.avatar-text {
  font-size: 10px;
  font-weight: bold;
  color: white;
}

/* Container da mensagem */
.message-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

/* Informações do autor */
.message-author {
  font-size: 12px;
  font-weight: 600;
  color: var(--v-primary-base, #1976d2);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.admin-badge {
  background: var(--v-primary-base, #1976d2);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

/* Bolha da mensagem */
.message-bubble {
  background: white;
  border: 1px solid #e1e8ed;
  border-radius: 18px;
  padding: 10px 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  word-wrap: break-word;
  max-width: 100%;
}

.message-bubble.bubble-own {
  background: var(--v-success-base, linear-gradient(135deg, #4caf50 0%, #388e3c 100%));
  color: white;
  border: none;
  border-bottom-right-radius: 4px;
}

.message-bubble.bubble-admin {
  background: var(--v-primary-lighten5, #e7f3ff);
  border: 1px solid var(--v-primary-lighten3, rgba(25, 118, 210, 0.2));
  border-bottom-left-radius: 4px;
}

/* Conteúdo da mensagem */
.message-content {
  line-height: 1.4;
  font-size: 14px;
}

.message-content :deep(a) {
  color: inherit;
  text-decoration: underline;
}

.message-content :deep(p) {
  margin: 0;
}

/* Arquivos */
.message-files {
  margin-top: 8px;
}

.file-attachment {
  margin-top: 6px;
}

.file-image {
  max-width: 250px;
  max-height: 250px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: block;
}

.file-image:hover {
  transform: scale(1.02);
}

.file-download {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
}

.message-bubble.bubble-own .file-download {
  background: rgba(255, 255, 255, 0.2);
}

.file-download:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.message-bubble.bubble-own .file-download:hover {
  background: rgba(255, 255, 255, 0.3);
}

.file-download span {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
}

/* Horário da mensagem */
.message-time {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.message-bubble.bubble-own .message-time {
  color: rgba(255, 255, 255, 0.8);
  justify-content: flex-end;
}

.read-status {
  color: rgba(255, 255, 255, 0.8);
}

/* Responsividade */
@media (max-width: 768px) {
  .message-wrapper {
    max-width: 90%;
    gap: 6px;
    margin-bottom: 14px;
  }

  .avatar-circle {
    width: 28px;
    height: 28px;
  }

  .avatar-text {
    font-size: 9px;
  }

  .message-bubble {
    padding: 8px 12px;
    border-radius: 16px;
  }

  .message-content {
    font-size: 13px;
  }

  .file-image {
    max-width: 200px;
    max-height: 200px;
  }

  .file-download {
    min-width: 160px;
    padding: 8px 10px;
  }

  .file-download span {
    font-size: 12px;
  }

  .message-time {
    font-size: 10px;
    margin-top: 4px;
  }
}

@media (max-width: 480px) {
  .message-wrapper {
    max-width: 95%;
    gap: 4px;
    margin-bottom: 12px;
  }

  .avatar-circle {
    width: 26px;
    height: 26px;
  }

  .message-bubble {
    padding: 6px 10px;
    border-radius: 14px;
  }

  .message-content {
    font-size: 12px;
  }

  .file-image {
    max-width: 150px;
    max-height: 150px;
  }

  .file-download {
    min-width: 140px;
    padding: 6px 8px;
  }
}
</style>
